/** Reusable pulse skeleton primitives for loading states */

interface SkeletonBarProps {
  className?: string;
  width?: string;
}

/** Single pulsing bar */
export function SkeletonBar({ className = "", width }: SkeletonBarProps) {
  return (
    <div
      className={`h-4 rounded bg-muted animate-pulse ${className}`}
      style={width ? { width } : undefined}
    />
  );
}

/** Skeleton for a detail page header (title + metadata row) */
export function SkeletonDetailHeader() {
  return (
    <div className="space-y-3 animate-pulse">
      <div className="h-6 w-2/5 rounded bg-muted" />
      <div className="flex gap-4">
        <div className="h-3.5 w-24 rounded bg-muted" />
        <div className="h-3.5 w-20 rounded bg-muted" />
        <div className="h-3.5 w-28 rounded bg-muted" />
      </div>
    </div>
  );
}

/** Skeleton for a list of items (e.g. control tree, card list) */
export function SkeletonList({ rows = 6, className = "" }: { rows?: number; className?: string }) {
  return (
    <div className={`space-y-3 animate-pulse ${className}`}>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="flex items-center gap-3">
          <div className="size-4 rounded bg-muted flex-shrink-0" />
          <div
            className="h-4 rounded bg-muted"
            style={{ width: `${55 + ((i * 17) % 35)}%` }}
          />
        </div>
      ))}
    </div>
  );
}

/** Skeleton for a card with title + a few lines */
export function SkeletonCard({ lines = 3 }: { lines?: number }) {
  return (
    <div className="rounded-lg border border-border bg-card p-5 space-y-3 animate-pulse">
      <div className="h-5 w-1/3 rounded bg-muted" />
      {Array.from({ length: lines }).map((_, i) => (
        <div
          key={i}
          className="h-3.5 rounded bg-muted"
          style={{ width: `${65 + ((i * 23) % 30)}%` }}
        />
      ))}
    </div>
  );
}

/** Skeleton for a two-column detail panel (control tree + detail) */
export function SkeletonControlPanel() {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <div className="lg:col-span-1 rounded-lg border border-border bg-card p-4">
        <div className="h-4 w-28 rounded bg-muted mb-4 animate-pulse" />
        <SkeletonList rows={8} />
      </div>
      <div className="lg:col-span-2 rounded-lg border border-border bg-card p-5">
        <div className="space-y-4 animate-pulse">
          <div className="flex items-center gap-2">
            <div className="h-5 w-16 rounded bg-muted" />
            <div className="h-5 w-24 rounded bg-muted" />
          </div>
          <div className="h-5 w-2/5 rounded bg-muted" />
          <div className="space-y-2 mt-4">
            <div className="h-3.5 w-full rounded bg-muted" />
            <div className="h-3.5 w-4/5 rounded bg-muted" />
            <div className="h-3.5 w-3/5 rounded bg-muted" />
          </div>
        </div>
      </div>
    </div>
  );
}

/** Full-page centered skeleton for page-level loading */
export function SkeletonPage() {
  return (
    <div className="space-y-6 animate-pulse">
      <div className="h-7 w-1/4 rounded bg-muted" />
      <div className="h-4 w-2/5 rounded bg-muted" />
      <div className="rounded-lg border border-border bg-card p-5 space-y-4">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="flex items-center gap-4">
            <div className="h-4 w-1/4 rounded bg-muted" />
            <div className="h-4 w-1/6 rounded bg-muted" />
            <div className="h-4 w-1/5 rounded bg-muted" />
            <div className="h-4 w-16 rounded bg-muted" />
          </div>
        ))}
      </div>
    </div>
  );
}
